/**
 * Section Script Loader
 * 
 * Loads section-specific JavaScript based on data attributes.
 * Uses eager glob imports to avoid dynamic import issues with Shopify's CDN.
 */

// Eagerly import all section modules using Vite's glob import
const sectionModules = import.meta.glob('../sections/*.js', { eager: true });

// Create a map of section name to module
const sectionMap = {};
for (const path in sectionModules) {
  // Extract section name from path (e.g., '../sections/header.js' -> 'header')
  const name = path.match(/\/([^/]+)\.js$/)?.[1];
  if (name) {
    sectionMap[name] = sectionModules[path];
  }
}

/**
 * Load section-specific scripts
 */
export function loadSectionScripts() {
  const sections = document.querySelectorAll('[data-section-script]');
  
  sections.forEach((section) => {
    const scriptName = section.dataset.sectionScript;
    
    if (scriptName) {
      loadSectionScript(scriptName, section);
    }
  });
}

/**
 * Load a specific section script
 */
function loadSectionScript(scriptName, element) {
  try {
    const module = sectionMap[scriptName];
    
    if (!module) {
      // Section script doesn't exist, silently skip
      return;
    }
    
    if (element.dataset.sectionLoaded) return;
    element.dataset.sectionLoaded = 'true';

    if (module.default) {
      // If the module exports a default class/function, instantiate it
      if (typeof module.default === 'function') {
        new module.default(element);
      }
    } else if (module.init) {
      // If the module exports an init function, call it
      module.init(element);
    }
    
    // console.log(`📦 Loaded section script: ${scriptName}`);
  } catch (error) {
    console.warn(`Failed to load section script: ${scriptName}`, error);
  }
}

/**
 * Observer for dynamically added sections (for theme editor)
 */
export function observeSections() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.hasAttribute('data-section-script')) {
          const scriptName = node.dataset.sectionScript;
          loadSectionScript(scriptName, node);
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Initialize section observer
observeSections();

